// src/invoices.js
import { useEffect, useMemo, useState } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import InvoiceList from './invoicelist';
import InvoiceEntry from './invoiceentry';
import InvoicePrint from './invoiceprint';
import { createDocumentDuplicateDraft } from './documentDuplicateDraft';
import TitleBar from './components/ui/TitleBar';
import BackToDashboardButton from './components/ui/BackToDashboardButton';
import OperationalListControls from './components/OperationalListControls';
import SavedToastOverlay from './components/SavedToastOverlay';

/* ===============================
   HELPERS
================================== */
const safeUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const addDaysISO = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
};

const computeStatus = (inv) => {
  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || 0);
  const balance = Math.max(0, total - paid);

  const s = String(inv.status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (balance <= 0 && total > 0) return 'paid';
  if (paid > 0 && balance > 0) return 'partial';

  const due = inv.due_date || inv.invoice_date || todayISO();
  const dueDate = new Date(String(due).slice(0, 10) + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (now > dueDate && balance > 0) return 'overdue';
  return s || 'unpaid';
};

const normalizeInvoice = (inv) => {
  const invoice_date = String(inv.invoice_date || todayISO()).slice(0, 10);
  const due_date = String(inv.due_date || invoice_date).slice(0, 10);

  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || 0);
  const balance_due = Math.max(0, total - paid);

  const terms = (inv.terms || inv.customer_terms || 'COD');

  const fixed = {
    ...inv,
    invoice_date,
    due_date,
    terms,
    amount_paid: paid,
    balance_due
  };

  fixed.status = computeStatus(fixed);
  return fixed;
};

const normalizeInvoiceFromDb = (dbInv) => {
  let items = [];
  try {
    if (Array.isArray(dbInv.items_json)) {
      items = dbInv.items_json;
    } else if (dbInv.items_json) {
      const parsed = JSON.parse(dbInv.items_json);
      if (Array.isArray(parsed)) items = parsed;
    }
  } catch (_e) {}

  return normalizeInvoice({
    id: dbInv.external_id || dbInv.id,
    database_id: dbInv.id,
    external_id: dbInv.external_id || null,
    invoice_number: dbInv.invoice_number || '',
    invoice_date: dbInv.invoice_date,
    due_date: dbInv.due_date,
    first_viewed_at: dbInv.first_viewed_at || null,

    subtotal: Number(dbInv.subtotal || 0),
    discount: (() => {
      const tag = String(dbInv.notes || '').match(/\[DISCOUNT:([^\]]+)\]/);
      return tag ? tag[1] : Number(dbInv.discount || 0);
    })(),
    discount_selection: dbInv.discount_selection || '',
    total: Number(dbInv.total || 0),

    amount_paid: Number(dbInv.amount_paid || 0),
    expected_amount_paid: Number(dbInv.amount_paid || 0),
    balance_due: Number(dbInv.balance_due || 0),

    status: String(dbInv.status || 'UNPAID').toLowerCase(),

    sales_tax_rate: Number(dbInv.sales_tax_rate || 0),
    county_sales_tax_rate: Number(dbInv.county_sales_tax_rate || 0),
    county_name: dbInv.county_name || '',
    state_abbr: dbInv.state_abbr || '',
    state_name: dbInv.state_name || '',
    stateTaxAmount: Number(dbInv.stateTaxAmount || 0),
    countyTaxAmount: Number(dbInv.countyTaxAmount || 0),
    selected_local_tax_ids: Array.isArray(dbInv.selected_local_tax_ids)
      ? dbInv.selected_local_tax_ids.map((id) => Number(id))
      : null,

    terms: dbInv.terms || 'COD',

    customer_id: dbInv.customer_id ?? null,
    customer_name: dbInv.customer_name || '',
    company: dbInv.company || '',
    email: dbInv.customer_email || '',
    office_phone: dbInv.customer_phone || '',
    mobile_phone: dbInv.customer_phone || '',
    address: dbInv.customer_address || '',
    notes: dbInv.notes || '',
    from_estimate_id: dbInv.from_estimate_id || null,

    items
  });
};

const dueFromTerms = (invoiceDateISO, terms) => {
  const t = (terms || 'COD').toString().toUpperCase().trim();
  if (t === 'NET15' || t === 'NET 15') return addDaysISO(invoiceDateISO, 15);
  if (t === 'NET30' || t === 'NET 30') return addDaysISO(invoiceDateISO, 30);
  return invoiceDateISO;
};


/* ===============================
   CUSTOMER TERMS LOOKUP (DB FIRST)
================================== */
const getCustomerTermsById = (customers, customerId) => {
  try {
    if (!customerId) return null;
    const list = Array.isArray(customers) ? customers : [];
    const found = list.find(c => String(c.id) === String(customerId));
    const t = found?.terms || found?.customer_terms || found?.customerTerms || null;
    return t ? String(t).toUpperCase().trim() : null;
  } catch (e) {
    return null;
  }
};

/* ===============================
   SERVER SYNC
================================== */
const mapInvoiceToServerPayload = (inv) => {
  const n = normalizeInvoice(inv);

  const parseDiscountSelection = (sel, subtotal) => {
    try {
      const s = String(sel || "0").trim();
      if (!s || s === "0") return { amount: 0, meta: "" };

      const sub = Number(subtotal || 0);
      const clampDiscount = (amount) => {
        const discount = Number.isFinite(amount) ? amount : 0;
        return Math.min(Math.max(discount, 0), sub);
      };

      if (!s.includes(":")) {
        const v = Number(s);
        return { amount: clampDiscount(v), meta: "" };
      }

      const parts = s.split(":");
      const type = String(parts[0] || "fixed").toLowerCase();
      const rawVal = Number(parts[1] || 0);
      const id = parts[2] ? String(parts[2]).trim() : "";

      const val = Number.isFinite(rawVal) ? rawVal : 0;
      const rawDiscount = (type === "percentage") ? (sub * (val / 100)) : val;

      return {
        amount: clampDiscount(rawDiscount),
        meta: [type, String(val), id].filter(Boolean).join(":")
      };
    } catch (e) {
      return { amount: 0, meta: "" };
    }
  };

  const subtotal = Number(n.subtotal || 0);
  const disc = parseDiscountSelection(n.discount, subtotal);
  const discount = Number(disc.amount || 0);
  const total = Number(n.total || 0);
  const paid = Number(n.amount_paid || 0);
  const balance_due = Math.max(0, total - paid);

  const stateTaxAmount = Number(n.stateTaxAmount || 0);
  const countyTaxAmount = Number(n.countyTaxAmount || 0);

  const status = computeStatus({ ...n, balance_due });
  const statusUpper =
    status === 'paid' ? 'PAID' :
    status === 'partial' ? 'PARTIAL' :
    status === 'overdue' ? 'OVERDUE' :
    'UNPAID';

  return {
    external_id: String(n.id),
    inventory_accounting_version: 1,
    expected_amount_paid: n.expected_amount_paid,
    expected_invoice_id: n.database_id,

    // IMPORTANT: blank allowed; backend assigns sequence if blank
    invoice_number: String(n.invoice_number || ''),

    status: statusUpper,

    subtotal: Number.isFinite(subtotal) ? subtotal : 0,
    discount: Number.isFinite(discount) ? discount : 0,
    discount_selection: String(n.discount_selection || disc.meta || '0'),
    total: Number.isFinite(total) ? total : 0,

    amount_paid: Number.isFinite(paid) ? paid : 0,
    balance_due: Number.isFinite(balance_due) ? balance_due : 0,

    sales_tax_rate: Number(n.sales_tax_rate || 0),
    county_sales_tax_rate: Number(n.county_sales_tax_rate || 0),
    stateTaxAmount: Number.isFinite(stateTaxAmount) ? stateTaxAmount : 0,
    countyTaxAmount: Number.isFinite(countyTaxAmount) ? countyTaxAmount : 0,

    invoice_date: String(n.invoice_date || '').slice(0, 10),
    due_date: String(n.due_date || '').slice(0, 10),
    terms: String(n.terms || 'COD'),

    customer_id: n.customer_id ?? null,
    customer_name: String(n.customer_name || ''),
    company: String(n.company || ''),
    customer_email: String(n.email || n.customer_email || ''),
    customer_phone: String(n.mobile_phone || n.office_phone || n.customer_phone || ''),
    customer_address: String(n.address || n.customer_address || ''),

    notes: (() => {
      const base = String(n.notes || '');
      const meta = String(disc.meta || '').trim();
      if (!meta) return base;
      if (base.includes('[DISCOUNT:')) return base;
      return (base ? base + "\n" : "") + `[DISCOUNT:${meta}]`;
    })(),

    items_json: JSON.stringify(Array.isArray(n.items) ? n.items : []),

    payment_method: String(n.last_payment_method || n.payment_method || ''),
    payment_ref:    String(n.last_payment_ref    || n.payment_ref    || ''),
    county_name:    String(n.county_name || ''),
    state_abbr:     String(n.state_abbr || ''),
    state_name:     String(n.state_name || ''),
    selected_local_tax_ids: Array.isArray(n.selected_local_tax_ids)
      ? [...n.selected_local_tax_ids]
      : null,
    from_estimate_id: n.from_estimate_id ? String(n.from_estimate_id) : null
  };
};

const upsertInvoiceToServer = async (inv) => {
  try {
    const API = getApiBase();
    const payload = mapInvoiceToServerPayload(inv);

    const res = await fetch(`${API}/api/invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
      body: JSON.stringify(payload)
    });

    let data = null;
    try {
      const ct = String(res.headers.get('content-type') || '');
      if (ct.toLowerCase().includes('application/json')) {
        const parsed = await res.json();
        data = parsed && typeof parsed === 'object' ? parsed : null;
      }
    } catch (e) { console.error(e); }

    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        code: data?.code,
        error: data?.error || 'Invoice failed to save. Please check your connection and try again.',
        ...(data?.outstanding_cents !== undefined ? { outstanding_cents: data.outstanding_cents } : {})
      };
    }

    return data || { ok: true };
  } catch (e) {
    console.error(e);
    return { ok: false, error: 'Save could not be confirmed. Refresh and review the invoice before submitting again.' };
  }
};

const deleteInvoiceOnServer = async (invoiceId, reason, deletedBy, restoreInventory) => {
  try {
    const API = getApiBase();
    const body = { reason: reason || '', deleted_by: deletedBy || 'Unknown' };
    if (typeof restoreInventory === 'boolean') body.restore_inventory = restoreInventory;
    const res = await fetch(`${API}/api/invoices/${encodeURIComponent(String(invoiceId))}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, error: data.error || null };
  } catch (e) {
    console.error(e);
    return { ok: false, error: 'Network error — please try again.' };
  }
};

/* ===============================
   DB FEED NORMALIZERS (for pickers)
================================== */
const normalizeInventoryForPicker = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => {
    const name =
      r?.name ??
      r?.partName ??
      r?.part_name ??
      r?.item_name ??
      r?.product_name ??
      r?.title ??
      '';

    const costNum = Number(r?.cost ?? 0);
    const markupNum = Number(r?.markup ?? 0);
    const computedRetail =
      Number.isFinite(costNum) && Number.isFinite(markupNum)
        ? costNum * (1 + markupNum / 100)
        : 0;

    const retail =
      r?.retail ??
      r?.retail_price ??
      r?.retailPrice ??
      r?.sale_price ??
      r?.salePrice ??
      r?.sell_price ??
      r?.sellPrice ??
      r?.price ??
      r?.rate ??
      computedRetail;

    return { ...r, name, retail };
  });
};

const normalizeJobsForPicker = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => {
    const jobName =
      r?.jobName ??
      r?.job_name ??
      r?.name ??
      r?.title ??
      '';

    const price =
      r?.price ??
      r?.rate ??
      r?.amount ??
      0;

    return { ...r, jobName, price };
  });
};

/* ===============================
   COMPONENT
================================== */
const Invoices = ({ openNewInvoice = false, onNewInvoiceOpened, openInvoiceId, openInvoiceMode, invoiceDraft, onInvoiceOpened, navigate } = {}) => {
  const [view, setView] = useState(openNewInvoice ? 'entry' : 'list'); // list | entry | print
  const [printInvoice, setPrintInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceListYear, setInvoiceListYear] = useState(() => new Date().getFullYear());
  const [invoiceListSearchDraft, setInvoiceListSearchDraft] = useState('');
  const [invoiceListSearch, setInvoiceListSearch] = useState('');
  const [invoiceListPage, setInvoiceListPage] = useState(1);
  const [invoiceListPagination, setInvoiceListPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 1
  });
  const [availableInvoiceYears, setAvailableInvoiceYears] = useState([]);
  const [invoiceListVersion, setInvoiceListVersion] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [cannedJobs, setCannedJobs] = useState([]);

  const [counties, setCounties] = useState([]);
  const [coupons,  setCoupons]  = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (!openNewInvoice) return;
    if (typeof onNewInvoiceOpened === 'function') onNewInvoiceOpened();
  }, [openNewInvoice, onNewInvoiceOpened]);

  // NEW invoices: invoice_number blank, backend assigns.
  const createFreshInvoice = () => {
    const invoice_date = todayISO();
    const due_date = invoice_date;
    return normalizeInvoice({
      id: safeUUID(),
      invoice_number: '',

      invoice_date,
      due_date,
      terms: 'COD',

      customer_id: null,
      customer_name: '',
      company: '',
      email: '',
      office_phone: '',
      mobile_phone: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      address: '',
      notes: '',

      items: [],
      subtotal: 0,
      discount: 0,
      sales_tax_rate: 0,
      county_sales_tax_rate: 0,
      stateTaxAmount: 0,
      countyTaxAmount: 0,
      total: 0,

      county_name: '',
      selected_local_tax_ids: [],

      amount_paid: 0,
      expected_amount_paid: 0,
      balance_due: 0,

      status: 'unpaid'
    });
  };

  const [invoice, setInvoice] = useState(() => createFreshInvoice());
  const [capturedInvoiceDraft, setCapturedInvoiceDraft] = useState(null);

  const handleDuplicateInvoice = (sourceInvoice) => {
    const freshInvoice = createFreshInvoice();
    const duplicate = createDocumentDuplicateDraft({
      documentType: 'invoice',
      sourceDocument: sourceInvoice,
      freshDocument: freshInvoice,
      createId: safeUUID
    });
    const invoice_date = freshInvoice.invoice_date;
    const due_date = dueFromTerms(invoice_date, duplicate.terms);

    setInvoice(normalizeInvoice({
      ...duplicate,
      invoice_date,
      due_date,
      subtotal: Number(sourceInvoice.subtotal || 0),
      stateTaxAmount: Number(sourceInvoice.stateTaxAmount || 0),
      countyTaxAmount: Number(sourceInvoice.countyTaxAmount || 0),
      total: Number(sourceInvoice.total || 0)
    }));
    setView('entry');
  };

  const styles = {
    container: { padding: '18px', maxWidth: '1400px', margin: '0 auto', color: 'black' },
    headerRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },

    titleBar: {
      backgroundColor: 'white',
      color: 'black',
      border: '8px solid black',
      boxShadow: 'inset 0 0 0 6px #E8761D',
      padding: '12px 18px',
      fontWeight: 900,
      fontSize: '28px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      justifyContent: 'space-between',
      flexWrap: 'wrap'
    },

    newBtn: {
      backgroundColor: '#1B4332',
      color: '#FFA500',
      padding: '18px 34px',
      fontWeight: 900,
      border: '6px solid #FFA500',
      cursor: 'pointer',
      fontSize: '20px',
      textTransform: 'uppercase',
      transition: 'all 0.15s ease-in-out'
    },

    backBtn: {
      fontWeight: 900,
      padding: '10px 20px',
      border: '4px solid black',
      backgroundColor: '#FFA500',
      color: 'black',
      marginBottom: '15px',
      cursor: 'pointer',
      transition: 'all 0.15s ease-in-out'
    },

    printBar: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' },
    printBtn: { backgroundColor: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 18px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    editBtn:  { backgroundColor: '#1B4332', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 18px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    note: { fontWeight: 900, marginBottom: '10px' }
  };

  const phonePageCss = `
    @media (max-width: 767px) {
      .jfn-invoices-page {
        padding-left: 6px !important;
        padding-right: 6px !important;
      }
    }
  `;

  const replaceInvoiceInCurrentPage = (updatedInvoice) => {
    setInvoices((currentInvoices) => currentInvoices.map((currentInvoice) => (
      String(currentInvoice.id) === String(updatedInvoice.id)
        ? normalizeInvoice(updatedInvoice)
        : currentInvoice
    )));
  };

  const refreshInvoiceList = () => {
    setInvoiceListVersion((version) => version + 1);
  };

  const handleConvertBackToEstimate = async (inv) => {
    try {
      const extId = inv.external_id || inv.id;
      const API = getApiBase();
      const response = await fetch(`${API}/api/invoices/${encodeURIComponent(String(extId))}/convert-back`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({ deleted_by: currentUser?.email || 'Unknown' })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        alert(result.error || 'Convert back failed.');
        return;
      }
      if (invoices.length === 1 && invoiceListPage > 1) {
        setInvoiceListPage((page) => page - 1);
      } else {
        refreshInvoiceList();
      }
      if (navigate) {
        navigate('estimates');
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent('jobflow:saved', {
            detail: { message: 'INVOICE CONVERTED BACK TO ESTIMATE!' }
          }));
        }, 0);
      }
    } catch (e) {
      console.error(e);
      alert('Network error — please try again.');
    }
  };

  /* ===============================
     DB FEEDS
  ================================== */
  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        const API = getApiBase();

        const [cRes, iRes, taxRes, discRes] = await Promise.all([
          fetch(`${API}/api/customers`,            { headers: { ...getTenantHeaders() } }),
          fetch(`${API}/api/inventory`,            { headers: { ...getTenantHeaders() } }),
          fetch(`${API}/api/settings/taxes/local`, { headers: { ...getTenantHeaders() } }),
          fetch(`${API}/api/settings/discounts`,   { headers: { ...getTenantHeaders() } })
        ]);

        const jRes = await fetch(`${API}/api/canned-jobs`, { headers: { ...getTenantHeaders() } }).catch(() => null);

        if (cRes && cRes.ok) {
          const cData = await cRes.json();
          setCustomers(Array.isArray(cData) ? cData : []);
        } else setCustomers([]);

        if (iRes && iRes.ok) {
          const iData = await iRes.json();
          setInventory(normalizeInventoryForPicker(iData));
        } else setInventory([]);

        if (jRes && jRes.ok) {
          const jData = await jRes.json();
          setCannedJobs(normalizeJobsForPicker(jData));
        } else setCannedJobs([]);

        if (taxRes && taxRes.ok) {
          const taxData = await taxRes.json();
          setCounties(Array.isArray(taxData) ? taxData : []);
        } else setCounties([]);

        if (discRes && discRes.ok) {
          const discData = await discRes.json();
          setCoupons(Array.isArray(discData) ? discData.filter(d => d.isCoupon) : []);
        } else setCoupons([]);

        fetch(`${API}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, ...getTenantHeaders() }
        }).then(r => r.json()).then(d => { if (d.ok) setCurrentUser(d.user); }).catch(() => {});
      } catch (e) {
        setCustomers([]);
        setInventory([]);
        setCannedJobs([]);
        setCounties([]);
        setCoupons([]);
      }
    };

    fetchFeeds();
  }, []);

  /* ===============================
     Fetch invoices from DB
  ================================== */
  useEffect(() => {
    if (!invoiceListSearchDraft.trim()) {
      setInvoiceListSearch('');
      setInvoiceListPage(1);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setInvoiceListSearch(invoiceListSearchDraft);
      setInvoiceListPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [invoiceListSearchDraft]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInvoices = async () => {
      try {
        const API = getApiBase();
        const query = new URLSearchParams({
          page: String(invoiceListPage),
          page_size: '50'
        });

        if (invoiceListSearch.trim()) {
          query.set('search', invoiceListSearch);
        } else {
          query.set('year', String(invoiceListYear));
        }

        const response = await fetch(`${API}/api/invoices?${query.toString()}`, {
          headers: { ...getTenantHeaders() },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Failed to fetch invoices');
        const data = await response.json();

        const normalized = (data.rows || []).map(normalizeInvoiceFromDb);
        setInvoices(normalized);
        setInvoiceListPagination(data.pagination || {
          page: 1,
          page_size: 50,
          total: 0,
          total_pages: 1
        });
        setAvailableInvoiceYears(
          Array.isArray(data.available_years)
            ? data.available_years.map(Number).filter((year) => Number.isInteger(year))
            : []
        );
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('Failed to load invoices from database:', e);
        setInvoices([]);
      }
    };

    fetchInvoices();
    return () => controller.abort();
  }, [invoiceListYear, invoiceListSearch, invoiceListPage, invoiceListVersion]);

  const hydrateReturnedInvoiceNumber = (normalized, upList, serverResponse) => {
    try {
      if (!serverResponse || typeof serverResponse !== 'object') return { hydrated: null, list: upList };

      const returnedNumber = String(serverResponse.invoice_number || serverResponse.invoiceNumber || '').trim();
      if (!returnedNumber) return { hydrated: null, list: upList };

      const hydrated = normalizeInvoice({
        ...normalized,
        invoice_number: returnedNumber,
        database_id: serverResponse.database_id ?? normalized.database_id,
        amount_paid: serverResponse.amount_paid,
        expected_amount_paid: serverResponse.amount_paid,
        balance_due: serverResponse.balance_due,
        status: serverResponse.status
      });
      const up2 = [hydrated, ...upList.filter(i => String(i.id) !== String(hydrated.id))];
      return { hydrated, list: up2 };
    } catch (e) {
      return { hydrated: null, list: upList };
    }
  };

  const handleSaveInvoice = async (inv) => {
    const wasCreation = !String(inv.invoice_number || '').trim();
    const invoice_date = String(inv.invoice_date || todayISO()).slice(0, 10);

    const termsFromCustomer = getCustomerTermsById(customers, inv.customer_id);
    const terms = (inv.terms || inv.customer_terms || termsFromCustomer || 'COD');

    const due_date = dueFromTerms(invoice_date, terms);

    const normalized = normalizeInvoice({
      ...inv,
      invoice_date,
      terms,
      customer_terms: terms,
      due_date,
      invoice_number: String(inv.invoice_number || '')
    });

    const up1 = [normalized, ...invoices.filter(i => String(i.id) !== String(normalized.id))];
    const saved = await upsertInvoiceToServer(normalized);
    if (!saved?.ok) {
      alert(saved?.error || 'Invoice failed to save. Please check your connection and try again.');
      return;
    }
    const result = hydrateReturnedInvoiceNumber(normalized, up1, saved);

    if (result.hydrated) {
      setInvoice((prev) => {
        if (!prev) return result.hydrated;
        if (String(prev.id) !== String(result.hydrated.id)) return prev;
        return result.hydrated;
      });
    }

    refreshInvoiceList();
    setView('list');

    if (saved?.ok === true && String(saved.invoice_number || '').trim()) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: wasCreation ? 'INVOICE CREATED!' : 'INVOICE UPDATED!' }
      }));
    }
  };

  const invoiceWithTermsDue = useMemo(() => {
    const invoice_date = String(invoice.invoice_date || todayISO()).slice(0, 10);
    const termsFromCustomer = getCustomerTermsById(customers, invoice.customer_id);
    const terms = (invoice.terms || invoice.customer_terms || termsFromCustomer || 'COD');
    const due_date = dueFromTerms(invoice_date, terms);
    return normalizeInvoice({ ...invoice, invoice_date, terms, customer_terms: terms, due_date });
  }, [invoice, customers]);

  useEffect(() => {
    if (!invoiceDraft || typeof invoiceDraft !== 'object' || Array.isArray(invoiceDraft)) return undefined;

    setInvoice(normalizeInvoiceFromDb(invoiceDraft));
    setView('entry');
    setCapturedInvoiceDraft(invoiceDraft);
    return undefined;
  }, [invoiceDraft]);

  useEffect(() => {
    if (!capturedInvoiceDraft) return undefined;

    if (typeof onInvoiceOpened === 'function') onInvoiceOpened();
    setCapturedInvoiceDraft(null);
    return undefined;
  }, [capturedInvoiceDraft, onInvoiceOpened]);

  useEffect(() => {
    if (invoiceDraft && typeof invoiceDraft === 'object' && !Array.isArray(invoiceDraft)) return undefined;
    if (!openInvoiceId) return undefined;

    let cancelled = false;
    const openRequestedInvoice = async () => {
      let found = invoices.find(i =>
        String(i.id) === String(openInvoiceId) ||
        String(i.external_id || '') === String(openInvoiceId) ||
        String(i.database_id || '') === String(openInvoiceId)
      );

      if (!found) {
        try {
          const API = getApiBase();
          const res = await fetch(
            `${API}/api/customers/invoices/${encodeURIComponent(String(openInvoiceId))}`,
            { headers: { ...getTenantHeaders() } }
          );
          if (!res.ok) throw new Error('Invoice not found');
          const dbInv = await res.json();
          found = normalizeInvoiceFromDb(dbInv);
        } catch (_e) {
          if (!cancelled) {
            alert('Invoice could not be opened.');
            if (typeof onInvoiceOpened === 'function') onInvoiceOpened();
          }
          return;
        }
      }

      if (!found || cancelled) return;
      if (typeof onInvoiceOpened === 'function') onInvoiceOpened();
      if (openInvoiceMode === 'print') {
        setPrintInvoice(normalizeInvoice(found));
        setView('print');
      } else {
        setInvoice(normalizeInvoice(found));
        setView('entry');
      }
    };

    openRequestedInvoice();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, invoiceDraft, openInvoiceId, openInvoiceMode]);

  if (view === 'list') {
    return (
      <div className="jfn-invoices-page" style={styles.container}>
        <SavedToastOverlay accentColor="#B7410E" />

        <style>{`
          ${phonePageCss}
          .jf-new-btn:hover {
            background-color: #FFC107 !important;
            border-color: #FFA500 !important;
            color: black !important;
          }
          .jf-back-btn:hover {
            background-color: #FFC107 !important;
            border-color: black !important;
            color: black !important;
          }
        `}</style>

        <div style={{ ...styles.headerRow, width: '100%' }}>
          <TitleBar
            icon="💰"
            title={`INVOICES (${Number(invoiceListPagination.total || 0)})`}
            accent="#B7410E"
            right={
              <div style={{ display: 'flex', gap: '12px', flexDirection: window.innerWidth < 768 ? 'column' : 'row', alignItems: window.innerWidth < 768 ? 'stretch' : 'center' }}>
                <BackToDashboardButton onClick={() => (window.location.href = '/dashboard')} />

                <button
                  className="jf-new-btn"
                  style={styles.newBtn}
                  onClick={() => {
                    const fresh = createFreshInvoice();
                    setInvoice(fresh);
                    setView('entry');
                  }}
                >
                  + CREATE INVOICE
                </button>
              </div>
            }
          />
        </div>

        <InvoiceList
          invoices={invoices.map(normalizeInvoice)}
          isGlobalSearchActive={Boolean(invoiceListSearch.trim())}
          listControls={
            <OperationalListControls
              searchPlaceholder="Cust, Inv #, Phone, Email"
              showResultCount={false}
              theme={{
                border: '#B7410E',
                accent: '#E8784A',
                buttonBackground: '#B7410E',
                buttonText: 'white',
                buttonHover: '#E8784A',
                divider: '#B7410E'
              }}
              selectedYear={invoiceListYear}
              availableYears={availableInvoiceYears}
              search={invoiceListSearchDraft}
              isSearchActive={Boolean(invoiceListSearch.trim())}
              pagination={invoiceListPagination}
              onYearChange={(year) => {
                setInvoiceListYear(year);
                setInvoiceListPage(1);
              }}
              onSearchChange={setInvoiceListSearchDraft}
              onPageChange={(page) => {
                if (page < 1 || page > Number(invoiceListPagination.total_pages || 1)) return;
                setInvoiceListPage(page);
              }}
            />
          }
          currentUser={currentUser}
          onEdit={(inv) => {
            setInvoice(normalizeInvoice(inv));
            setView('entry');
          }}
          onDuplicate={handleDuplicateInvoice}
          onDelete={async (id, reason, deletedBy, restoreInventory) => {
            const result = await deleteInvoiceOnServer(id, reason, deletedBy, restoreInventory);
            if (!result.ok) {
              alert(result.error || 'Delete failed.');
              return;
            }
            if (invoices.length === 1 && invoiceListPage > 1) {
              setInvoiceListPage((page) => page - 1);
            } else {
              refreshInvoiceList();
            }
          }}
          onViewPrint={(inv) => {
            setPrintInvoice(normalizeInvoice(inv));
            setView('print');
          }}
          onPaymentReversed={(updatedInv) => {
            replaceInvoiceInCurrentPage(updatedInv);
          }}
          onRecordPayment={async (updatedInv) => {
            const normalized = normalizeInvoice(updatedInv);
            const saved = await upsertInvoiceToServer(normalized);
            if (!saved?.ok) return saved;

            const accepted = {
              ...normalizeInvoice({ ...normalized, amount_paid: Number(saved.amount_paid) }),
              expected_amount_paid: Number(saved.amount_paid),
              balance_due: Number(saved.balance_due),
              status: String(saved.status || '').toLowerCase()
            };
            replaceInvoiceInCurrentPage(accepted);

            return {
              ...saved,
              ok: true,
              amount_paid: accepted.amount_paid,
              balance_due: accepted.balance_due,
              status: accepted.status
            };
          }}
          onConvertBackToEstimate={handleConvertBackToEstimate}
        />
      </div>
    );
  }

  if (view === 'print') {
    return (
      <div style={styles.container}>
        <SavedToastOverlay accentColor="#B7410E" />

        <style>{`
          .jf-new-btn:hover {
            background-color: #FFC107 !important;
            border-color: #FFA500 !important;
            color: black !important;
          }
          .jf-back-btn:hover {
            background-color: #FFC107 !important;
            border-color: black !important;
            color: black !important;
          }
          .jf-print-btn:hover { background-color: #FFC107 !important; border-color: #FFA500 !important; color: black !important; }
          .jf-edit-btn:hover  { background-color: #FFC107 !important; border-color: #FFA500 !important; color: black !important; }
          @media print {
            body * { visibility: hidden !important; }
            #jf-inv-print-area, #jf-inv-print-area * { visibility: visible !important; }
            #jf-inv-print-area { position: fixed !important; left: 0 !important; top: 0 !important; width: 100% !important; }
          }
        `}</style>

        <button className="jf-back-btn" style={styles.backBtn} onClick={() => setView('list')}>
          ← BACK
        </button>

        <div style={styles.printBar}>
          <button className="jf-print-btn" style={styles.printBtn} onClick={() => window.print()}>
            PRINT / SAVE PDF
          </button>
          <button
            className="jf-edit-btn"
            style={styles.editBtn}
            onClick={() => {
              if (printInvoice) setInvoice(normalizeInvoice(printInvoice));
              setView('entry');
            }}
          >
            EDIT THIS INVOICE
          </button>
        </div>

        <div style={styles.note}>
          TIP: On the print screen, choose "Save as PDF" to download.
        </div>

        <div id="jf-inv-print-area">
          <InvoicePrint invoice={printInvoice} />
        </div>
      </div>
    );
  }

  return (
    <div className="jfn-invoices-page" style={styles.container}>
      <SavedToastOverlay accentColor="#B7410E" />

      <style>{`
        ${phonePageCss}
        .jf-new-btn:hover {
          background-color: #FFC107 !important;
          border-color: #FFA500 !important;
          color: black !important;
        }
        .jf-back-btn:hover {
          background-color: #FFC107 !important;
          border-color: black !important;
          color: black !important;
        }
      `}</style>

      <button className="jf-back-btn" style={styles.backBtn} onClick={() => setView('list')}>
        ← BACK
      </button>

      <InvoiceEntry
        invoice={invoiceWithTermsDue}
        setInvoice={setInvoice}
        customers={customers}
        inventory={inventory}
        setInventory={setInventory}
        cannedJobs={cannedJobs}
        counties={counties}
        coupons={coupons}
        onSave={handleSaveInvoice}
      />
    </div>
  );
};

export default Invoices;
